import { useEffect, useMemo, useRef, useState } from 'react';
import { LIMITS } from '../../shared/validation.js';
import { TabSelector } from './TabSelector.jsx';

const normalizeText = (text) =>
  text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const CatalogoPanel = ({
  productos,
  cantidades,
  busqueda,
  loading,
  error,
  activeTab,
  onTabChange,
  onCantidadChange,
  onBusquedaChange,
  onLimpiar
}) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const searchInputRef = useRef(null);

  const productosSeleccionadosCount = useMemo(() => {
    return Object.values(cantidades).filter(qty => {
      const n = parseInt(qty);
      return !isNaN(n) && n > 0;
    }).length;
  }, [cantidades]);

  const scrollToPreview = () => {
    const previewPanel = document.querySelector('.preview-panel');
    if (previewPanel) {
      previewPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return productos;
    const searchNormalized = normalizeText(busqueda);
    return productos.filter(p =>
      normalizeText(p.nombre).includes(searchNormalized) ||
      String(p.codigo).toLowerCase().includes(searchNormalized) ||
      normalizeText(p.unidad || '').includes(searchNormalized)
    );
  }, [productos, busqueda]);

  const highlightMatch = (text) => {
    if (!busqueda.trim() || !text) return text;
    const source = String(text);
    const sourceNorm = normalizeText(source);
    const queryNorm = normalizeText(busqueda);
    const idx = sourceNorm.indexOf(queryNorm);
    if (idx === -1) return source;
    return (
      <>
        {source.slice(0, idx)}
        <mark className="search-highlight">{source.slice(idx, idx + queryNorm.length)}</mark>
        {source.slice(idx + queryNorm.length)}
      </>
    );
  };

  // Atajos de teclado: "/" enfoca búsqueda, Esc la limpia
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea';
      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      } else if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        onBusquedaChange('');
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBusquedaChange]);

  if (loading) {
    return (
      <div className="catalogo-panel">
        <TabSelector activeTab={activeTab} onTabChange={onTabChange} />
        <div className="empty-state">Cargando catálogo...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="catalogo-panel">
        <TabSelector activeTab={activeTab} onTabChange={onTabChange} />
        <div className="error-state">
          No se pudo cargar el catálogo
          <br />
          <small>
            Si abrís el archivo con file://, usá un servidor local (npm run dev).
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="catalogo-panel">
      <TabSelector activeTab={activeTab} onTabChange={onTabChange} />
      <div className="catalogo-content">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre, código o medida... (pulsa /)"
            maxLength={LIMITS.BUSQUEDA_MAX}
            value={busqueda}
            onChange={(e) => onBusquedaChange(e.target.value)}
            ref={searchInputRef}
            aria-label="Buscar productos"
          />
          {busqueda && (
            <button
              type="button"
              className="search-clear"
              onClick={() => onBusquedaChange('')}
              aria-label="Limpiar búsqueda"
              title="Limpiar (Esc)"
            >×</button>
          )}
        </div>

        {productosFiltrados.length === 0 ? (
          <div className="empty-state">No se encontraron productos</div>
        ) : (
          <table className="productos-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Código</th>
                <th>Producto</th>
                <th>Unidad</th>
                <th style={{ width: '100px', textAlign: 'right' }}>Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {productosFiltrados.map(producto => {
                const cantidad = cantidades[producto.id] || '';
                const isSelected = parseInt(cantidad) > 0;
                return (
                  <tr key={producto.id} className={isSelected ? 'selected' : ''}>
                    <td>{highlightMatch(producto.codigo)}</td>
                    <td>{highlightMatch(producto.nombre)}</td>
                    <td>{highlightMatch(producto.unidad)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        type="number"
                        className="cantidad-input"
                        min="0"
                        max={LIMITS.CANTIDAD_MAX}
                        step="1"
                        inputMode="numeric"
                        value={cantidad}
                        onChange={(e) => onCantidadChange(producto.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <button className="btn-limpiar" onClick={onLimpiar}>
          Limpiar pedido
        </button>
      </div>

      <button
        className={`btn-scroll-to-preview ${productosSeleccionadosCount > 0 ? 'visible' : ''}`}
        onClick={scrollToPreview}
        aria-label="Ver pedido"
        title="Ver pedido"
        aria-hidden={productosSeleccionadosCount === 0}
        tabIndex={productosSeleccionadosCount === 0 ? -1 : 0}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <span className="btn-scroll-badge">{productosSeleccionadosCount}</span>
      </button>

      <button
        className={`btn-scroll-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Volver arriba"
        title="Volver arriba"
        aria-hidden={!showScrollTop}
        tabIndex={showScrollTop ? 0 : -1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 15l-6-6-6 6" />
        </svg>
      </button>
    </div>
  );
};
